/**
 * Reference-layer visibility (Zustand): which read-only layers are shown.
 *
 * Keyed by layer id from the tenant's catalog (lib/layers.ts). Defaults come
 * from each layer's `defaultVisible`; a person's own choices are remembered
 * per jurisdiction in this browser and shared by the plan and community
 * maps. Toggling only changes layer visibility on the live map -- these
 * layers never affect painting, validation, or what gets submitted.
 */
import { create } from 'zustand';
import type { LayerDef } from '../lib/layers';

const STORAGE_PREFIX = 'rd:layers:';

function readStored(slug: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + slug);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(parsed)) if (typeof v === 'boolean') out[k] = v;
      return out;
    }
  } catch {
    // Storage unavailable or corrupt -- fall back to defaults.
  }
  return {};
}

function writeStored(slug: string, choices: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + slug, JSON.stringify(choices));
  } catch {
    // Non-fatal: the choice still applies for this visit.
  }
}

interface OverlayVisibilityState {
  tenantSlug: string | null;
  /** Layer id -> visible. */
  visibility: Record<string, boolean>;
  /** Only the person's explicit choices (persisted). */
  choices: Record<string, boolean>;
  initFromTenant: (slug: string, layers: LayerDef[]) => void;
  setVisible: (id: string, visible: boolean) => void;
}

export const useOverlayStore = create<OverlayVisibilityState>()((set, get) => ({
  tenantSlug: null,
  visibility: {},
  choices: {},

  initFromTenant: (slug, layers) => {
    const choices = get().tenantSlug === slug ? get().choices : readStored(slug);
    const visibility: Record<string, boolean> = {};
    for (const layer of layers) visibility[layer.id] = choices[layer.id] ?? layer.defaultVisible;
    set({ tenantSlug: slug, choices, visibility });
  },

  setVisible: (id, visible) => {
    const { tenantSlug, choices, visibility } = get();
    const nextChoices = { ...choices, [id]: visible };
    if (tenantSlug) writeStored(tenantSlug, nextChoices);
    set({ choices: nextChoices, visibility: { ...visibility, [id]: visible } });
  },
}));
